import { LoadingOverlay } from 'components/Basic/LoadingOverlay/LoadingOverlay';
import { MarkerIcon } from 'components/Basic/Map/MarkerIcon';
import { usePigeonMap } from 'components/Basic/Map/PigeonMap/usePigeonMap';
import { PointMapCard } from 'components/Basic/Map/PointMapCard';
import useDebounce from 'hooks/helpers/UseDebounce';
import { Map, Marker, Overlay, ZoomControl } from 'pigeon-maps';
import { MapProps } from 'types/map';

type PigeonMapProps = {
    mapTilesProvider?: (x: number, y: number, z: number, dpr?: number) => string;
    attribution?: JSX.Element;
};

export const PigeonMap: FC<MapProps & PigeonMapProps> = ({
    mapTilesProvider,
    attribution,
    children,
    markers,
    clickOnMarkerHandler,
    shouldShowSpinner,
    clusterRadius,
    shouldChangeFitToBounds = true,
    minimumFitToBoundsZoom,
}) => {
    const {
        handleBoundsChanged,
        onPointClickHandler,
        clusters,
        center,
        zoom,
        activePoint,
        activeMarkerUuid,
        isActivePointValidForPopup,
        mapContainerRef,
    } = usePigeonMap({
        markers,
        clickOnMarkerHandler,
        shouldChangeFitToBounds,
        minimumFitToBoundsZoom,
        clusterRadius,
    });

    // Optimize animation
    const debouncedClusters = useDebounce(clusters, 350);

    return (
        <>
            <div className="h-full w-full" ref={mapContainerRef}>
                <Map
                    attribution={attribution}
                    center={[center.lat, center.lng]}
                    provider={mapTilesProvider}
                    zoom={zoom}
                    onBoundsChanged={handleBoundsChanged}
                >
                    {debouncedClusters.map((point) => {
                        const properties = point.properties;
                        const [lng, lat] = point.geometry.coordinates;
                        const isCluster = 'cluster' in properties && properties.cluster;
                        const pointCount = isCluster ? properties.point_count : 1;

                        return (
                            <Marker
                                key={isCluster ? `cluster-${point.id}` : properties.identifier}
                                anchor={[lat, lng]}
                                onClick={() => onPointClickHandler(properties.identifier, Number(point.id), isCluster)}
                            >
                                <MarkerIcon
                                    activeMarkerUuid={activeMarkerUuid || null}
                                    isCluster={properties.cluster}
                                    point={properties}
                                    totalPoints={debouncedClusters.length}
                                    totalPointsInCluster={pointCount}
                                />
                            </Marker>
                        );
                    })}
                    {activePoint && isActivePointValidForPopup && (
                        <Overlay
                            anchor={[activePoint.geometry.coordinates[1], activePoint.geometry.coordinates[0]]}
                            offset={[-5, 0]}
                        >
                            <PointMapCard activeMarker={activePoint.properties} />
                        </Overlay>
                    )}
                    <ZoomControl />
                    {children}
                </Map>
            </div>
            {shouldShowSpinner && <LoadingOverlay className="h-12 w-12" />}
        </>
    );
};
